/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
/* eslint-disable max-len */
import { Injectable, OnDestroy } from '@angular/core';
import { filter, map, tap } from 'rxjs/operators';
import { Agenda, createAgenda } from './agenda.model';
import { AgendaStore, DISPLAY_AGENDA, DOUBLE_VISUAL_AGENDA } from './agenda.store';
import moment from 'moment';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { ClinicQuery } from '@state/clinic/clinic.query';
import { FLOW_DATE_FORMAT, FLOW_TIME_FORMAT } from '@core/constants';
import { AesRequestModel } from '@core/models/AesRequest.model';
import { SettingsQuery } from '@state/settings/settings.query';
import { Service } from '@core/models/Service';
import { Consent } from '@core/models/Consent';
import { AgendaQuery } from '@state/agenda/agenda.query';
import { Cabinet, createCabinet } from '@state/agenda/cabinet.model';
import { LASER_SHOT } from '@core/settings';
import { Clinic } from '@state/clinic/clinic.model';
import { Pack } from '@core/models/Pack';
import { ApiService } from '@core/services/api/api.service';
import { ClientClinicHistSketch, createCabinetSketches } from '@core/models/ClientClinicHist';
import { SessionQuery } from '../session/session.query';
import { ClinicService } from '../clinic/clinic.service';
import { INF_APP_VIEW_FORM_MULTIPLE, INF_APP_VIEW_FORM_SIMPLE, INF_DIARY_APP_LOAD_PRO } from '@core/infs';

@Injectable({ providedIn: 'root' })
export class AgendaService implements OnDestroy {
    private readonly MAX_INT = 2147483647;

    private openSaturday: boolean;
    private openSunday: boolean;
    private clinicSubscription: Subscription;

    constructor(
        private agendaQuery: AgendaQuery,
        private agendaStore: AgendaStore,
        private apiService: ApiService,
        private clinicQuery: ClinicQuery,
        private clinicService: ClinicService,
        private settingsQuery: SettingsQuery,
        private sessionQuery: SessionQuery
    ) {
        this.clinicSubscription = this.clinicQuery.selectActive()
            .pipe(filter(clinic => clinic !== undefined))
            .subscribe(({ openSaturday, openSunday }) => {
                this.openSaturday = openSaturday;
                this.openSunday = openSunday;
            });
    }

    add(agenda: Agenda | Agenda[]): void {
        this.agendaStore.add(agenda);
    }

    setCurrentAgenda(agenda: Agenda): void {
        this.agendaStore.update({
            currentAgenda: agenda
        });
    }

    setMovingAgenda(agenda: Agenda): void {
        this.agendaStore.update({
            movingAgenda: agenda
        });
    }

    remove(id?: string): void {
        this.agendaStore.remove(id);
    }

    getAppointmentById(id: string, multiAppId: number = 0): Observable<Agenda> {
        const form = new AesRequestModel('SEC_APP_VIEW_FORM.V4', [
            { Param: 'AppID', Value: id, AESIndex: '1' },
            { Param: 'ActiveVisual', Value: this.agendaQuery.getValue().doubleDisplay.toString(), AESIndex: '7' },
            { Param: 'MultiAppId', Value: multiAppId.toString(), AESIndex: '8' }
        ], 9);

        return this.apiService.callApiObservable(form)
            .pipe(
                map(response => JSON.parse(response)),
                map(response => {
                    switch (response.inf) {
                        case INF_DIARY_APP_LOAD_PRO:
                        case INF_APP_VIEW_FORM_SIMPLE:
                            this.setCurrentAgenda(this.buildAgenda(response.data));
                            break;
                        case INF_APP_VIEW_FORM_MULTIPLE:
                            this.setCurrentAgenda(this.buildMultipleAgenda(id, response.data));
                            break;
                    }
                    return this.agendaQuery.getCurrentAgenda();
                })
            );
    }

    getCabinet(appointmentId: string): Observable<Cabinet> {
        const clinic = this.clinicQuery.getActive();
        const form = new AesRequestModel('SEC_LASER_LOAD_FORM', [
            { Param: 'DiaryGID', Value: appointmentId, AESIndex: '1' }
        ]);

        return this.apiService.callApiObservable(form)
            .pipe(
                map((res) => JSON.parse(res)),
                map(({ data }) => createCabinet(data, clinic.services))
            );
    }

    getDiaryByDate(diaryDate: moment.Moment, offset: number = 0): Observable<Agenda[]> {
        const currentMoment: moment.Moment = diaryDate.clone();
        const clinic: Clinic = this.clinicQuery.getActive();

        if (offset !== 0) {
            diaryDate.add(offset, 'd');
        }

        // Checks if selected day is saturday and if clinic supports saturday appointments
        if (diaryDate.day() === 6 && !this.openSaturday) {
            /*
                If we are looking for next day, currentMoment < diaryDate so we need to
                add one day to diaryDate to push to sunday, otherwise if we are looking
                for previous day, currentMoment > diaryDate so we need to subtract one
                day to go to friday.
                Check for offset === 0 because changing date from datepicker, offset = 0
             */
            diaryDate.add(currentMoment < diaryDate || offset === 0 ? 1 : -1, 'd');
        }

        // Checks if selected day is sunday and if clinic supports sunday appointments
        if (diaryDate.day() === 0 && !this.openSunday) {
            /*
                If we are looking for next day, currentMoment < diaryDate so we need to
                add one day to diaryDate to push to monday, otherwise if we are looking
                for previous day, currentMoment > diaryDate so we need to subtract one
                day to go to saturday.
                Check for offset === 0 because changing date from datepicker, offset = 0
             */
            diaryDate.add(currentMoment < diaryDate || offset === 0 ? 1 : -1, 'd');
        }

        // Checks if selected day is saturday and if clinic supports saturday appointments
        // NOTE: This condition is only valid when we are navigating back and previously checked sunday and offset
        if (diaryDate.day() === 6 && !this.openSaturday && (currentMoment > diaryDate || offset === 0)) {
            diaryDate.add(-1, 'd');
        }

        /**
         * @see https://momentjs.com/docs/#/get-set/day/
         */
        const openingTime: moment.Moment = this.isWeekday(diaryDate) ? clinic.weekdayOpeningTime : clinic.weekendOpeningTime;
        const closingTime: moment.Moment = this.isWeekday(diaryDate) ? clinic.weekdayClosingTime : clinic.weekendClosingTime;

        this.agendaStore.update({
            currentMoment: diaryDate.clone(),
            openingTime,
            closingTime
        });

        // Fix for diary multi appointment.
        // List view should work normally, but graphic agenda should use new SEC version
        let formName = 'SEC_DIARY_DAY_LOAD_FORM';

        if (this.agendaQuery.getValue().agendaDisplay === DISPLAY_AGENDA.GRAPHIC) {
            formName = this.agendaQuery.getValue().doubleDisplay === DOUBLE_VISUAL_AGENDA.PROFESSIONALS ? 'SEC_DIARY_DAY_LOAD_PRO_FORM.V2' : 'SEC_DIARY_DAY_LOAD_FORM.V2';
        }

        const form = new AesRequestModel(formName, [
            { Param: 'Date', Value: diaryDate.format(FLOW_DATE_FORMAT), AESIndex: '1' },
            { Param: 'UserID', Value: '0', AESIndex: '2' }
        ]);

        return this.apiService.callApiObservable(form)
            .pipe(
                map((res) => JSON.parse(res)),
                map(({ data }) => {
                    const agenda: Agenda[] = this.generateAgenda(data);
                    const movingAgenda = this.agendaQuery.getMovingAgenda();

                    if (movingAgenda) {
                        this.agendaQuery.getAll()
                            .filter(({ id }) => id !== movingAgenda.id)
                            .map(({ id }) => id)
                            .forEach(id => this.remove(id));
                    } else {
                        this.remove();
                    }

                    if (agenda.length > 0) {
                        this.add(agenda);
                    }

                    this.agendaStore.update({
                        protocolForce: data.ProtocolForceEnabled === '-1',
                        hasDoubleVisual: data.AppAllowByUser === '-1' && data.ProFormAllowed === '-1',
                    });

                    this.manageProfessionalCabinets(data);

                    return agenda;
                }
                )
            );
    }

    /**
     * Controla el estado del array de cabinas de profesional según la visual seleccionada y el valor actual de las cabinas
     * @param data resultado de la petición de la agenda
     */
    manageProfessionalCabinets(data) {
        let professionalCabinets = this.agendaQuery.getValue().professionalCabinets;

        switch (this.agendaQuery.getValue().doubleDisplay) {
            case DOUBLE_VISUAL_AGENDA.PROFESSIONALS:
                if (JSON.stringify(professionalCabinets) !== JSON.stringify(this.clinicService.buildProfessionalCabinets(data.__list_0))) {
                    professionalCabinets = this.clinicService.buildProfessionalCabinets(data.__list_0);
                    this.agendaStore.update({
                        professionalCabinets
                    });
                }
                break;
            case DOUBLE_VISUAL_AGENDA.CABINETS:
                this.agendaStore.update({
                    professionalCabinets: []
                });
                break;
        }
    }

    getSessionNumberFrom(clientId: string, productId: string): Observable<number> {
        const form = new AesRequestModel('SEC_LASER_DET_NUM_FORM', [
            {
                Param: 'Json',
                Value: JSON.stringify({
                    clientID: clientId,
                    productID: productId,
                    fieldNumber: '0' // Required for FrontEnd, not used in FlowwwMe
                }),
                AESIndex: '1'
            }
        ], 0);

        return this.apiService.callApiObservable(form).pipe(
            map(response => JSON.parse(response)),
            map(({ data }) => {
                const numSession = parseInt(data.numsession, 10);
                return numSession + 1;
            })
        );
    }

    deleteAppointment(agendaId: string): Observable<string> {
        const form = new AesRequestModel('SEC_APP_DEL_FORM', [
            { Param: 'DiaryId', Value: agendaId, AESIndex: '1' },
            { Param: 'Section', Value: '0', AESIndex: '2' }, // Used in FlowwwFE
            { Param: 'DeleteAllPackAppointments', Value: '0', AESIndex: '3' }
        ]);

        return this.apiService.callApiObservable(form)
            .pipe(tap(() => this.remove(agendaId)));
    }

    setNotAssisted(diaryId: string, reason: string): Observable<string> {
        const form = new AesRequestModel('SEC_APP_NOT_ATTENDED_FORM', [
            { Param: 'DiaryID', Value: diaryId, AESIndex: '1' },
            { Param: 'Section', Value: '0', AESIndex: '2' },
            { Param: 'Comments', Value: reason, AESIndex: '3' }
        ]);

        return this.apiService.callApiObservable(form)
            .pipe(tap(() => this.remove(diaryId)));
    }

    signAppointment(id: string, image: string): Observable<any> {
        const form = new AesRequestModel('SEC_LASER_PDF_BASIC_SIGN_FORM', [
            { Param: 'LaserGID', Value: id, AESIndex: '1' },
            { Param: 'SignatureB64', Value: image, AESIndex: '2' }
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map(response => JSON.parse(response))
        );
    }

    //TODO: Meter aquí croquis? Creo que no porque he visto que SIEMPRE que se pasa por cabina se va por el LASER no como en front
    validateAppointment(appointment: Agenda): Observable<any> {
        const form = new AesRequestModel('SEC_TREATMENT_SAVE_FORM', [
            { Param: 'DiaryGID', Value: appointment.id, AESIndex: '1' },
            { Param: 'TicketMergeConfirmed', Value: '2', AESIndex: '2' }
        ]);

        return this.apiService.callApiObservable(form).pipe(
            map(response => JSON.parse(response),
                map(({ data }) => data))
        );
    }

    validateLaserAppointment(appointment: Agenda, cabinetData: Cabinet): Observable<any> {
        const Form = 'SEC_LASER_SAVE_FORM';
        const Data = [
            { Param: 'CabinetNum', Value: cabinetData.number, AESIndex: '1' },
            { Param: 'ClientID', Value: cabinetData.clientId, AESIndex: '2' },
            { Param: 'DiaryGID', Value: appointment.id, AESIndex: '3' },
            { Param: 'LaserGID', Value: cabinetData.laserId, AESIndex: '4' },
            { Param: 'Start', Value: appointment.start.format(FLOW_TIME_FORMAT), AESIndex: '5' },
            { Param: 'End', Value: appointment.end.format(FLOW_TIME_FORMAT), AESIndex: '6' },
            { Param: 'Comments', Value: cabinetData.comments, AESIndex: '7' },
            { Param: 'Delayed', Value: '0', AESIndex: '8' },
            { Param: 'UserID', Value: cabinetData.professionalId, AESIndex: '9' },
            { Param: 'TicketMergeConfirmed', Value: '2', AESIndex: '10' },
            { Param: 'ConsentsArray', Value: '', AESIndex: '11' },
            { Param: 'GetConsumptionProducts', Value: cabinetData.getConsumptionProducts, AESIndex: '12' },
            { Param: 'ConsumptionProductsJson', Value: cabinetData.consumptionProducts, AESIndex: '13' },
            { Param: 'Drawings', Value: cabinetData.drawings.map((sketch) => sketch.idSketch + '*' + sketch.b64).join('|'), AESIndex: 'SKETCHES' },
            { Param: 'ClinicAnnotation', Value: cabinetData.clinicAnnotation, AESIndex: 'ANNOTATION' },
        ];

        const cabinetRequiresShot = this.settingsQuery.getValue().cabinetRequiresShot;
        const cabinetRequiresDCD = this.settingsQuery.getValue().cabinetRequiresDCD;

        let aesIndex = 13;
        for (const cabinetService of cabinetData.services) {

            const laserZoneId = cabinetService.service ? cabinetService.service.id : '0';
            if (!cabinetService.isTreatment && laserZoneId === '0') {
                return throwError(new Error('val_session_param'));
            }

            Data.push({
                Param: 'LaserZoneID',
                Value: laserZoneId,
                AESIndex: `${++aesIndex}`
            });

            const laserSessionId = cabinetService.session ? cabinetService.session : '0';
            if (!cabinetService.isTreatment && laserSessionId === '0') {
                return throwError(new Error('val_session_param'));
            }

            Data.push({
                Param: 'LaserSessionID',
                Value: laserSessionId,
                AESIndex: `${++aesIndex}`
            });

            const equipmentId = cabinetService.equipment ? cabinetService.equipment : '0';
            if (!cabinetService.isTreatment && equipmentId === '0') {
                return throwError(new Error('val_session_param'));
            }

            Data.push({
                Param: 'EquipmentID',
                Value: equipmentId,
                AESIndex: `${++aesIndex}`
            });

            const paramI = cabinetService.pI ? cabinetService.pI : '0';
            if (!cabinetService.isTreatment && (paramI === '0' || paramI === '-1')) {
                return throwError(new Error('val_session_param'));
            }

            Data.push({
                Param: 'ParamI',
                Value: paramI,
                AESIndex: `${++aesIndex}`
            });

            const paramII = cabinetService.pII ? cabinetService.pII : '0';
            if (!cabinetService.isTreatment && (paramII === '0' || paramII === '-1')) {
                return throwError(new Error('val_session_param'));
            }

            Data.push({
                Param: 'ParamII',
                Value: paramII,
                AESIndex: `${++aesIndex}`
            });

            const paramIII = cabinetService.pIII ? cabinetService.pIII : '0';
            if (!cabinetService.isTreatment && (paramIII === '0' || paramIII === '-1')) {
                return throwError(new Error('val_session_param'));
            }

            Data.push({
                Param: 'ParamIII',
                Value: paramIII,
                AESIndex: `${++aesIndex}`
            });

            if (cabinetRequiresDCD) {
                const paramIV = cabinetService.pIV ? cabinetService.pIV : '0';
                if (!cabinetService.isTreatment && (paramIV === '0' || paramIV === '-1')) {
                    return throwError(new Error('val_session_param'));
                }

                Data.push({
                    Param: 'ParamIV',
                    Value: paramIV,
                    AESIndex: `${++aesIndex}`
                });
            }

            if (cabinetRequiresShot !== LASER_SHOT.DEACTIVATED_FIELDS) {
                // eslint-disable-next-line max-len
                const shotStart = parseInt(!cabinetService.shotStart || cabinetService.shotStart === '' ? '0' : cabinetService.shotStart, 10);
                const shotEnd = parseInt(!cabinetService.shotEnd || cabinetService.shotEnd === '' ? '0' : cabinetService.shotEnd, 10);

                if (!cabinetService.isTreatment) {
                    if (isNaN(shotStart) || isNaN(shotEnd)) {
                        return throwError(new Error('val_session_param'));
                    }

                    if (shotStart > this.MAX_INT || shotEnd > this.MAX_INT) {
                        return throwError(new Error('val_session_param'));
                    }

                    if (shotEnd <= shotStart) {
                        return throwError(new Error('val_laser_shot_range'));
                    }
                }

                Data.push({
                    Param: 'ShotStart',
                    Value: shotStart.toString(),
                    AESIndex: `${++aesIndex}`
                });

                Data.push({
                    Param: 'ShotEnd',
                    Value: shotEnd.toString(),
                    AESIndex: `${++aesIndex}`
                });
            }
        }

        const request = new AesRequestModel(Form, Data);

        return this.apiService.callApiObservable(request)
            .pipe(
                map(response => JSON.parse(response)),
                map(({ inf, data }) => ({ inf, data })));
    }

    ngOnDestroy(): void {
        this.clinicSubscription.unsubscribe();
    }

    setGridLoaded(gridLoaded: boolean): void {
        this.agendaStore.update({ gridLoaded });
    }

    setAgendaDisplay(agendaDisplay: DISPLAY_AGENDA): void {
        this.agendaStore.update({ agendaDisplay });
    }

    setDoubleDisplay(doubleDisplay: DOUBLE_VISUAL_AGENDA): void {
        this.agendaStore.update({ doubleDisplay });
    }

    saveAgendaComments(agendaId: string, comments: string): Promise<string> {
        const form = new AesRequestModel('SEC_APP_COMMENTS_EDIT_FORM', [
            {
                Param: 'AgendaId',
                Value: agendaId,
                AESIndex: '1'
            },
            {
                Param: 'Comments',
                Value: comments,
                AESIndex: '2'
            }
        ]);

        return this.apiService.callApiObservable(form)
            .pipe(map(res => JSON.parse(res))).toPromise();
    }

    getCabinetSketches(appointmentId: string, servicesIds: string[]): Observable<ClientClinicHistSketch[]> {
        const form = new AesRequestModel('SEC_CABINET_SKETCH_LIST_LOAD_FORM', [
            {
                Param: 'Json',
                Value: JSON.stringify({
                    diaryGID: appointmentId,
                    servicesID: servicesIds.join(',')
                }),
                AESIndex: '1'
            }
        ], 0);

        return this.apiService.callApiObservable(form).pipe(
            map((response) => JSON.parse(response)),
            map(({ data }) => {
                const { aesUrl, sessionId } = this.sessionQuery.getValue();
                return createCabinetSketches(data?.cabinetSketches ? JSON.parse(data.cabinetSketches) : [], aesUrl, sessionId);
            })
        );
    }

    private buildAgenda(data: any): Agenda {
        const timeSlot = this.settingsQuery.getValue().timeSlot;
        const clinicPacks = this.clinicQuery.getActive().packs;

        const packs = new Set<Pack>();
        const services = data.__list_1;
        const consents = data.__list_2;
        let signedConsents = true;

        const listServices: Service[] = services.map(service => {
            const pack = clinicPacks.find(({ id }) => id === service.packid);
            if (pack) {
                packs.add(pack);
            }

            return {
                fromPack: service.packid,
                id: service.zoneid,
                name: service.productdesc,
                slots: parseInt(service.zoneslots, 10),
                voucherId: service.voucherid,
                time: moment.duration(parseInt(service.zoneslots, 10) * timeSlot, 'm')
            } as Service;
        });

        const listConsents: Consent[] = consents.map(consent => {
            signedConsents = signedConsents && consent.signed === '-1';

            if (!signedConsents &&
                consent.FLOWww_List &&
                consent.FLOWww_List.name === 'SignedSessions' &&
                consent.FLOWww_List.FLOWww_Item) {

                consent.signed = '-1';
                consent.signDate = consent.FLOWww_List.FLOWww_Item.signDate;
                signedConsents = true;
            }

            return {
                id: consent.id,
                name: consent.description,
                signed: consent.signed !== '0',
                session: consent.isSession !== '0',
                signDate: moment(consent.signDate, FLOW_DATE_FORMAT),
                isEditable: consent.iseditable !== '0',
                isDoctor: consent.isdoctor !== '0'
            } as Consent;
        });

        //Una cita puede marcarse como no asisistida cuando:
        //- Setting SET_APP_NOTASSITED -1
        //- NO sea nueva
        //- NO esté confirmada
        //- Si la cita es a futuro (día mañana en adelante), además tener setting SET_APP_NOTASSISTED_FUTURE -1
        const canNotAssistedApp =
            this.settingsQuery.getValue().appNotAssisted &&
            data.AppID &&
            // eslint-disable-next-line eqeqeq
            data.AppConfirmed == '0' &&
            (moment(data.AppDate_ShortDate, FLOW_DATE_FORMAT) <= moment() || this.settingsQuery.getValue().appNotAssistedFuture);

        return {
            id: data.AppID,
            cabinetNumber: data.CabinetNum,
            cabinetCode: data.CabinetCode,
            client: {
                id: data.ClientID,
                name: data.ClientName,
                phone: data.ClientPhone,
                sex: data.ClientSex
            },
            color: '',
            comments: data.AppComments,
            confirmed: data.AppConfirmed,
            date: moment(data.AppDate_ShortDate, FLOW_DATE_FORMAT),
            equipConfirm: data.EquipConfirm !== '0',
            end: moment(data.AppEnd, FLOW_TIME_FORMAT),
            blockAgenda: false,
            blockedId: '',
            laserGid: '',
            listConsents,
            listServices,
            listPacks: [...packs],
            name: '',
            otherUser: false,
            professionalCabinets: [],
            professionalId: data.UserID,
            professionalName: data.UserName,
            signedConsents,
            start: moment(data.AppStart, FLOW_TIME_FORMAT),
            tags: data.AppTags.split('|').filter(tagId => tagId !== ''),
            ticketGClosed: data.TicketGClosed === '-1',
            userAvConfirm: data.UserAvConfirm === '-1',
            userEaConfirm: data.UserEaConfirm === '-1',
            userOvConfirm: data.UserOvConfirm === '-1',
            userSkConfirm: data.UserSkConfirm === '-1',
            fwa: false, // Estos valores no vienen en la respuesta
            migrated: false,
            delayed: false,
            notAttended: false,
            // Check this on the response when implementing multiapp
            multiAppointmentId: +data.AppMultID,
            multiAppointments: [],
            confirmTag: '0',
            canNotAssistedApp,
            doctorValidated: false,
            hasDoubleVisual: true,
            partialValidated: false,
            cabinetAbbr: data.CabinetAbbr,
        } as Agenda;
    }

    private buildMultipleAgenda(agendaId: string, data: any): Agenda {
        return {
            id: agendaId,
            cabinetNumber: data.DiaryGMultCabinetID,
            cabinetCode: data.DiaryGMultCabinetName,
            color: '',
            date: moment(data.DiaryGMultDate, FLOW_DATE_FORMAT),
            end: moment(data.DiaryGMultTimeEnd, FLOW_TIME_FORMAT),
            professionalCabinets: [],
            professionalId: data.DiaryGMultUserID,
            professionalName: data.DiaryGMultUserName,
            start: moment(data.DiaryGMultTimeStart, FLOW_TIME_FORMAT),
            multiAppointmentId: parseInt(data.DiaryGMultID, 10),
            multiAppointments: data.AppointmentData ? this.generateMultiAppointments(JSON.parse(data.AppointmentData)) : null,
            hasDoubleVisual: true,
            cabinetAbbr: data.DiaryGMultCabinetAbbr,
        } as Agenda;
    }

    private isWeekday(day: moment.Moment): boolean {
        return day.day() > 0 && day.day() < 6;
    }

    /**
     * Genera el objeto de la agenda según el tipo de visual
     * @param data Datos de la petición de agenda
     * @returns Agenda[]
     */
    private generateAgenda(data) {
        if (this.agendaQuery.getValue().doubleDisplay === DOUBLE_VISUAL_AGENDA.PROFESSIONALS && this.agendaQuery.getValue().agendaDisplay === DISPLAY_AGENDA.GRAPHIC) {
            return data.__list_1.map(item => createAgenda(item));
        } else {
            return data.__list_0.map(item => createAgenda(item));
        }
    }

    private generateMultiAppointments(data) {
        const multiAppointments = data.map((agenda: any) => ({
            client: agenda.Client,
            confirmed: agenda.Confirmed === '-1',
            doctorConfirmed: agenda.DoctorConfirmed === '-1',
            id: agenda.Id,
            serviceName: agenda.ServiceName.join(', '),
            cabinName: agenda.CabinName,
            cabinId: agenda.CabinId,
            professionalName: agenda.ProfessionalName,
            professionalId: agenda.ProfessionalId,
            start: agenda.Start,
            end: agenda.End
        }));

        return multiAppointments;
    }
}
